import { Breadcrumb, Button, Dropdown, Empty, Input, Layout, Modal, notification, Select, Space, Spin, Table, theme } from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";

import { CheckCircleOutlined, CloseCircleOutlined, DownOutlined, FileDoneOutlined, QrcodeOutlined } from "@ant-design/icons";
import { Html5Qrcode } from "html5-qrcode";
import { Helmet } from "react-helmet";
import { apiListEventsPay, apiPaymentDownload, apiValidateTicket } from "../../../api/events";
import './EventPay.scss';

const EventPay = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const { id } = useParams();
  const { name } = useParams();
  const [loading, setLoading] = useState(true);
  // PAYMENT LIST
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [payments, setPayments] = useState([]);

  // Paginação
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  // QR Code Validation
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isScannerModalOpen, setIsScannerModalOpen] = useState(false);
  const [isManualInputModalOpen, setIsManualInputModalOpen] = useState(false);
  const [manualInputValue, setManualInputValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [waitingForPermission, setWaitingForPermission] = useState(true);
  const [validatingTicket, setValidatingTicket] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationSource, setValidationSource] = useState(null); // 'scanner' or 'manual'
  const scannerRef = useRef(null);

  // Função de busca
  const handleSearch = (value) => {
    setSearchInput(value);
  };

  // Aguarda o usuário parar de digitar antes de buscar
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput);
      setPagination(prev => (prev.current === 1 ? prev : { ...prev, current: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Filtros ativos, compartilhados entre a listagem e o relatório
  const activeFilters = () => {
    const filters = {};
    if (searchText) {
      filters.search = searchText;
    }
    if (statusFilter) {
      filters.status = statusFilter;
    }
    return filters;
  };

  // Função de busca com paginação
  useEffect(() => {
    if (!id) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    apiListEventsPay(id, {
      ...activeFilters(),
      page: pagination.current,
      page_size: pagination.pageSize,
    })
      .then((json) => {
        if (cancelled) {
          return;
        }
        setPayments(json.results || []);
        setPagination(prev => ({ ...prev, total: json.count || 0 }));
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        notification.error({
          message: 'Erro',
          description: 'Ocorreu um erro ao buscar os dados.',
          placement: 'topRight',
        });
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, searchText, statusFilter, pagination.current, pagination.pageSize]);

  const handleTableChange = (nextPagination) => {
    setPagination(prev => ({
      ...prev,
      current: nextPagination.current,
      pageSize: nextPagination.pageSize,
    }));
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value === undefined ? '' : value);
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
  };

  const statusTranslations = {
    FREE: "Gratuito",
    EXPIRED: "Expirado",
    PENDING: "Pendente",
    PAID: "Pago",
    CANCELED: "Cancelado"
  };

  const columnsPayment = [
    {
      title: 'Nome',
      dataIndex: ['customer', 'name'],
      key: 'name',
      render: (name, { status }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={`status-color ${statusTranslations[status]}`}>{statusTranslations[status]}</span>
      )
    },
    {
      title: 'Email',
      dataIndex: ['customer', 'email'],
      key: 'email',
      responsive: ["lg"],
    },
    {
      title: 'Valor',
      dataIndex: 'total',
      key: 'total',
      render: total => `R$ ${(total / 100).toFixed(2)}`,
      responsive: ["lg"],
    },
    {
      title: 'Data da compra',
      dataIndex: 'created_at',
      key: 'created_at',
      render: created_at => new Date(created_at).toLocaleString(),
      responsive: ["xl"],
    },
    {
      title: '',
      key: 'action',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/evento/compras/detalhe/${id}/${record.id}`)} >
          Visualizar
        </Button>
      ),
    },
  ];

  const items = [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ];

  const handleDownload = async (format) => {
    try {
      const blob = await apiPaymentDownload(id, format, activeFilters());
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `detalhes-pagamento-${name}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  const handleMenuClick = (e) => {
    handleDownload(e.key);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  // QR Code handlers
  const handleOpenValidationModal = () => {
    setIsValidationModalOpen(true);
  };

  const handleCloseValidationModal = () => {
    setIsValidationModalOpen(false);
  };

  const handleOpenScanner = () => {
    setIsValidationModalOpen(false);
    setIsScannerModalOpen(true);
    setWaitingForPermission(true);
    setTimeout(() => {
      startScanner();
    }, 100);
  };

  const validateTicket = async (ticketCheckoutId, source = 'scanner') => {
    setValidatingTicket(true);
    setValidationSource(source);

    try {
      const result = await apiValidateTicket(id, ticketCheckoutId);

      if (result.success) {
        setValidationResult({
          success: true,
          message: 'Ingresso validado com sucesso!'
        });
      } else {
        // Handle different error types
        let errorMessage = '';

        // Check if error contains UUID validation message
        if (result.error && result.error.includes('não é um UUID válido')) {
          errorMessage = 'Ingresso não encontrado.';
        } else {
          switch (result.error) {
            case 'ticketCheckoutId is required':
              errorMessage = 'O código do ingresso é obrigatório.';
              break;
            case 'Ticket checkout not found':
              errorMessage = 'Ingresso não encontrado.';
              break;
            case 'Checkout is not paid':
              errorMessage = 'Este ingresso não foi pago.';
              break;
            case 'Ticket has already been used':
              errorMessage = 'Este ingresso já foi utilizado.';
              break;
            default:
              errorMessage = result.error || 'Erro ao validar o ingresso.';
          }
        }

        setValidationResult({
          success: false,
          message: errorMessage
        });
      }
    } catch (error) {
      console.error('Error validating ticket:', error);
      setValidationResult({
        success: false,
        message: 'Ocorreu um erro ao validar o ingresso. Tente novamente.'
      });
    } finally {
      setValidatingTicket(false);
    }
  };

  const startScanner = () => {
    if (!scanning) {
      setScanning(true);
      const html5QrCode = new Html5Qrcode("qr-reader");

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      html5QrCode.start(
        { facingMode: "environment" }, // Use rear camera on mobile
        config,
        async (decodedText) => {
          // QR Code successfully scanned
          stopScanner(html5QrCode);
          await validateTicket(decodedText);
        },
        () => {
          // parse error, ignore it.
        }
      ).then(() => {
        // Camera started successfully
        setWaitingForPermission(false);
      }).catch((err) => {
        console.error("Error starting scanner:", err);
        notification.error({
          message: 'Erro',
          description: 'Não foi possível iniciar a câmera.',
          placement: 'topRight',
        });
        setScanning(false);
        setIsScannerModalOpen(false);
        setWaitingForPermission(false);
      });

      scannerRef.current = html5QrCode;
    }
  };

  const stopScanner = (html5QrCode) => {
    const scanner = html5QrCode || scannerRef.current;
    if (scanner) {
      // Check if scanner is actually running before trying to stop
      const state = scanner.getState();
      if (state === 2 || state === 3) { // 2 = SCANNING, 3 = PAUSED
        scanner.stop().then(() => {
          setScanning(false);
          scannerRef.current = null;
        }).catch((err) => {
          console.error("Error stopping scanner:", err);
          setScanning(false);
          scannerRef.current = null;
        });
      } else {
        setScanning(false);
        scannerRef.current = null;
      }
    }
  };

  const handleCloseScannerModal = () => {
    stopScanner();
    setIsScannerModalOpen(false);
    setScanning(false);
    setWaitingForPermission(true);
    setValidationResult(null);
    scannerRef.current = null;
  };

  const handleScanAnother = () => {
    setValidationResult(null);
    setWaitingForPermission(true);
    setTimeout(() => {
      startScanner();
    }, 100);
  };

  const handleExitValidation = () => {
    handleCloseScannerModal();
    handleCloseManualInputModal();
  };

  const handleOpenManualInput = () => {
    setIsValidationModalOpen(false);
    setIsManualInputModalOpen(true);
    setManualInputValue('');
  };

  const handleCloseManualInputModal = () => {
    setIsManualInputModalOpen(false);
    setManualInputValue('');
    setValidationResult(null);
  };

  const handleManualInputSubmit = async () => {
    if (manualInputValue.trim()) {
      await validateTicket(manualInputValue.trim(), 'manual');
    }
  };

  const handleTypeAnother = () => {
    setValidationResult(null);
    setManualInputValue('');
  };

  return (
    <div className='EventPay' style={{ backgroundColor: "#f5f5f5" }}>
      <Helmet>
        <title>Clap Tickets - Compras Eventos - {name}</title>
      </Helmet>
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Content
          style={{
            padding: '23px 32px 100px 32px',
            margin: "30px 0 0 0",
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Breadcrumb className="breadcrumb-title">
              <Breadcrumb.Item>Compras Evento - {name}</Breadcrumb.Item>
            </Breadcrumb>
            <Button
              type="primary"
              icon={<QrcodeOutlined />}
              onClick={handleOpenValidationModal}
            >
              Validar Ingressos
            </Button>
          </div>

          <div className="container-table">
            <Space style={{ marginBottom: 16 }} className="box-filters">
              <Input
                style={{ height: 32 }}
                placeholder="Pesquisar"
                value={searchInput}
                onChange={e => handleSearch(e.target.value)}
              />
              <Select
                placeholder="Filtrar por status"
                onChange={handleStatusChange}
                allowClear
                value={statusFilter}
                style={{ width: 200 }}
                options={[
                  { value: 'FREE', label: 'Gratuito' },
                  { value: 'EXPIRED', label: 'Expirado' },
                  { value: 'PENDING', label: 'Pendente' },
                  { value: 'PAID', label: 'Pago' },
                  { value: 'CANCELED', label: 'Cancelado' },
                  { value: '', label: 'Todos' },
                ]}
              />
            </Space>

            <Table
              columns={columnsPayment}
              dataSource={payments}
              loading={loading}
              rowKey="id"
              locale={{ emptyText: <Empty description="Sem dados" /> }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: false,
              }}
              onChange={handleTableChange}
            />
          </div>
          <div className="download-section">
            <Dropdown menu={menuProps} >
              <Button>
                <Space>
                  Baixar Relatório
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>

          {/* Validation Modal */}
          <Modal
            title="Validar Ingressos"
            open={isValidationModalOpen}
            onCancel={handleCloseValidationModal}
            footer={null}
            centered
            maskClosable={false}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                type="default"
                size="large"
                onClick={handleOpenManualInput}
                style={{ width: '100%' }}
              >
                Digitar Manualmente
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<QrcodeOutlined />}
                onClick={handleOpenScanner}
                style={{ width: '100%' }}
              >
                Escanear
              </Button>
            </div>
          </Modal>

          {/* Scanner Modal */}
          <Modal
            title={validationResult ? "Resultado da Validação" : "Escanear QR Code"}
            open={isScannerModalOpen}
            onCancel={handleCloseScannerModal}
            footer={null}
            centered
            width={400}
            maskClosable={false}
            styles={{ body: { padding: '24px', minHeight: '300px', position: 'relative' } }}
          >
            {validationResult ? (
              // Result View
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                {validationResult.success ? (
                  <CheckCircleOutlined style={{ fontSize: '80px', color: '#52c41a', marginBottom: '24px' }} />
                ) : (
                  <CloseCircleOutlined style={{ fontSize: '80px', color: '#ff4d4f', marginBottom: '24px' }} />
                )}
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: '#262626'
                }}>
                  {validationResult.success ? 'Sucesso!' : 'Erro na validação'}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#595959',
                  lineHeight: '1.6',
                  marginBottom: '32px'
                }}>
                  {validationResult.message}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', alignItems: 'center' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<QrcodeOutlined />}
                    onClick={handleScanAnother}
                    style={{ width: '100%', maxWidth: '300px' }}
                  >
                    Escanear outro ingresso
                  </Button>
                  <Button
                    size="large"
                    onClick={handleExitValidation}
                    style={{ width: '100%', maxWidth: '300px' }}
                  >
                    Sair
                  </Button>
                </div>
              </div>
            ) : (
              // Scanner View
              <>
                <div id="qr-reader" style={{ width: '100%' }}></div>
                {waitingForPermission && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    zIndex: 1000
                  }}>
                    <QrcodeOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '24px' }} />
                    <h3 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '12px', color: '#262626' }}>
                      Precisamos da sua permissão!
                    </h3>
                    <p style={{ fontSize: '14px', color: '#595959', lineHeight: '1.6', marginBottom: '8px' }}>
                      Para escanear o QR Code do ingresso, precisamos acessar a câmera do seu dispositivo.
                    </p>
                    <p style={{ fontSize: '14px', color: '#8c8c8c', lineHeight: '1.6' }}>
                      Por favor, autorize o acesso quando solicitado pelo navegador.
                    </p>
                  </div>
                )}
              </>
            )}
          </Modal>

          {/* Manual Input Modal */}
          <Modal
            title={validationResult ? "Resultado da Validação" : "Digitar Código do Ingresso"}
            open={isManualInputModalOpen}
            onCancel={handleCloseManualInputModal}
            footer={null}
            centered
            width={400}
            maskClosable={false}
          >
            {validationResult ? (
              // Result View
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                {validationResult.success ? (
                  <CheckCircleOutlined style={{ fontSize: '80px', color: '#52c41a', marginBottom: '24px' }} />
                ) : (
                  <CloseCircleOutlined style={{ fontSize: '80px', color: '#ff4d4f', marginBottom: '24px' }} />
                )}
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: '#262626'
                }}>
                  {validationResult.success ? 'Sucesso!' : 'Erro na validação'}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#595959',
                  lineHeight: '1.6',
                  marginBottom: '32px'
                }}>
                  {validationResult.message}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', alignItems: 'center' }}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleTypeAnother}
                    style={{ width: '100%', maxWidth: '300px' }}
                  >
                    Digitar outro código
                  </Button>
                  <Button
                    size="large"
                    onClick={handleExitValidation}
                    style={{ width: '100%', maxWidth: '300px' }}
                  >
                    Sair
                  </Button>
                </div>
              </div>
            ) : (
              // Input View
              <div style={{ padding: '20px 0' }}>
                <p style={{
                  fontSize: '14px',
                  color: '#595959',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  Digite o código do ingresso abaixo:
                </p>
                <Input
                  size="large"
                  placeholder="Código do ingresso"
                  value={manualInputValue}
                  onChange={(e) => setManualInputValue(e.target.value)}
                  onPressEnter={handleManualInputSubmit}
                  style={{ marginBottom: '16px' }}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={handleManualInputSubmit}
                  disabled={!manualInputValue.trim()}
                  style={{ width: '100%' }}
                >
                  Confirmar
                </Button>
              </div>
            )}
          </Modal>

          {/* Loading Overlay */}
          {validatingTicket && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}>
              <Spin size="large" />
              <p style={{ color: '#fff', marginTop: '16px', fontSize: '16px' }}>
                Validando ingresso...
              </p>
            </div>
          )}
        </Content >
      </Layout >
    </div >
  )
}

export default EventPay;
